import { z } from 'zod';
import { DEFAULT_PREFERENCES, preferencesSchema } from '@/shared/lib/preferences';
import { t } from '@/shared/i18n';

export const roleSchema = z.enum(['owner', 'admin', 'member', 'guest']);
export type Role = z.infer<typeof roleSchema>;

/** Названия ролей на языке интерфейса: читаются в момент обращения, поэтому геттеры. */
export const roleLabels: Readonly<Record<Role, string>> = {
  get owner() {
    return t('Владелец');
  },
  get admin() {
    return t('Администратор');
  },
  get member() {
    return t('Участник');
  },
  get guest() {
    return t('Гость');
  },
};

export const notifyLevelSchema = z.enum(['all', 'mentions', 'nothing']);
export type NotifyLevel = z.infer<typeof notifyLevelSchema>;
export const notifyPrefSchema = z.enum(['default', 'all', 'mentions', 'nothing']);
export type NotifyPref = z.infer<typeof notifyPrefSchema>;

export const userSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  // Контакты профиля: ник Telegram без «@» и «О себе» (старый сервер их не присылает).
  telegram: z.string().nullable().default(null),
  about: z.string().nullable().default(null),
  // Часовой пояс IANA (Europe/Moscow); null — не указан.
  timezone: z.string().nullable().default(null),
  totpEnabled: z.boolean(),
  // Ключи доступа (WebAuthn): сколько их, можно ли входить ими без пароля; twoFactor — есть ли второй шаг вообще.
  passkeys: z.number().int().default(0),
  passkeyLogin: z.boolean().default(true),
  twoFactor: z.boolean().optional(),
  // Сколько резервных кодов осталось; null — кодов нет.
  recoveryCodesLeft: z.number().int().nullable().default(null),
  // Когда человек сам менял пароль; null — не менял с первого входа.
  passwordChangedAt: z.string().nullable().default(null),
  avatarVersion: z.number().int().nullable(),
  // Личные настройки интерфейса; старый сервер их не присылает — берём значения по умолчанию.
  preferences: preferencesSchema.default(DEFAULT_PREFERENCES),
});
export type User = z.infer<typeof userSchema>;

/** Как идут дела с оплатой: всё в порядке, скоро продление, людей больше, чем мест, срок истёк. */
export const billingStateSchema = z.enum(['active', 'renewSoon', 'overLimit', 'expired']);
export const billingSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('cloud'),
    state: billingStateSchema,
    /** Платим за активных за месяц: сколько их сейчас. */
    activeSeats: z.number().int(),
    period: z.enum(['year', 'month']),
    /** Сумма ближайшего списания, ₽; null — на Free списаний нет. */
    nextAmount: z.number().nullable(),
    renewsAt: z.string().nullable(),
    /** «Visa •••• 4242» или «По счёту»; null — способа оплаты нет. */
    method: z.string().nullable(),
    invoices: z.array(z.object({ id: z.string(), date: z.string(), amount: z.number(), title: z.string() })),
    /** Сколько места занято, ГБ; нет сведений — полоса не рисуется. */
    storageUsedGb: z.number().optional(),
  }),
  z.object({
    kind: z.literal('onprem'),
    state: billingStateSchema,
    /** Ключ без середины: CHAT-7F3K-…-Q2LM. */
    key: z.string(),
    licensee: z.string(),
    users: z.number().int(),
    usersLimit: z.number().int(),
    issuedAt: z.string(),
    expiresAt: z.string(),
    /** Тариф в ключе: при истёкшем сроке пространство работает на Free. */
    licensedPlan: z.enum(['free', 'pro', 'enterprise']),
  }),
]);
export type Billing = z.infer<typeof billingSchema>;

// linkPreviews — показывать превью ссылок; bridgeUrl — адрес Bridge в полосе разделов; googleDomains — домены почты, с которых новых людей пускает вход через Google.
export const workspaceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  linkPreviews: z.boolean().default(true),
  passworkUrl: z.string().nullable().default(null),
  /** Свой адрес Bridge by Passwork; null — общий (features/services). */
  bridgeUrl: z.string().nullable().default(null),
  googleDomains: z.array(z.string()).default([]),
  /** Участники просят пригласить коллег, администратор одобряет; старый сервер поля не присылает — выключено. */
  inviteRequests: z.boolean().default(false),
  /** Тариф (features/plans): старый сервер не присылает — значит, тарифов ещё нет и открыто всё. */
  plan: z.enum(['free', 'pro', 'enterprise']).default('enterprise'),
  /** Демо-компании: каталог сервисов и нейросетей только российский (ru) или только зарубежный (intl). Сервер не присылает — показано всё. */
  region: z.enum(['ru', 'intl']).nullable().default(null),
  /** Где стоит сервер: наше облако или коробка в контуре компании. */
  deployment: z.enum(['cloud', 'onprem']).default('cloud'),
  /** Оплата: в облаке — места и списания, в коробке — лицензионный ключ. Сервер пока не присылает — показывает демо. */
  billing: billingSchema.nullable().default(null),
  /** Без второго шага входа участники пространство не откроют. */
  requireTwoFactor: z.boolean().default(false),
  /** Кто публикует документы по ссылке: никто, владелец и администраторы или любой, кто администрирует документ. */
  publicDocLinks: z.enum(['off', 'admins', 'members']).default('admins'),
  /** Кто заводит секретные чаты: все (кроме гостей), выбранные группы, только администраторы или никто. */
  secretChats: z.enum(['members', 'groups', 'admins', 'off']).default('members'),
  /** Группы, которым разрешены секретные чаты (режим groups). */
  secretChatGroups: z.array(z.string()).default([]),
});
export type Workspace = z.infer<typeof workspaceSchema>;

export const memberSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  telegram: z.string().nullable().default(null),
  about: z.string().nullable().default(null),
  timezone: z.string().nullable().default(null),
  title: z.string().nullable(),
  role: roleSchema,
  roleLabel: z.string(),
  avatarVersion: z.number().int().nullable(),
  statusEmoji: z.string().nullable(),
  statusText: z.string().nullable(),
  statusExpiresAt: z.string().nullable(),
  presence: z.enum(['auto', 'away']),
  online: z.boolean(),
  lastSeenAt: z.string().nullable(),
  dndUntil: z.string().nullable(),
  /** «Не беспокоить, пока не выключу»: срока нет (dndUntil — только предел). */
  dndOpen: z.boolean().optional(),
  /** Режим фокуса: тихий интерфейс у себя, у коллег — «в режиме фокуса до …». */
  focusUntil: z.string().nullable().default(null),
  /** Режим фокуса без срока: таймера нет, в календаре — событие «Фокус» без окончания. */
  focusOpen: z.boolean().optional(),
  deactivated: z.boolean(),
  /** Доступ отозван, оставлена только избранная переписка («Доступ закрыт»). */
  restricted: z.boolean().optional(),
  /** Личное исключение из правила «Кто заводит секретные чаты»: allow — можно, deny — нельзя, null — как в пространстве. */
  secretChats: z.enum(['allow', 'deny']).nullable().optional(),
  joinedAt: z.string(),
  /** Приложение (бот интеграции), а не человек. */
  bot: z.boolean().default(false),
  /** Человек из Slack (мост): профиль-тень с именем и фото, без входа. */
  bridge: z.boolean().optional(),
  /** Свои поля профиля, которые видит текущий человек: id поля → значение (только в списке людей). */
  profile: z.record(z.string(), z.string()).default({}),
});
export type Member = z.infer<typeof memberSchema>;

/** Ответ на «Пригласить»: новому человеку — ссылка-приглашение (переслать самому) и ушло ли письмо с ней. */
export const invitedMemberSchema = memberSchema.extend({ inviteLink: z.string().nullable().default(null), emailSent: z.boolean().default(false) });

export const myMembershipSchema = memberSchema.extend({
  workspace: workspaceSchema,
  notify: notifyLevelSchema,
  /** Гость из другой компании в общем канале: его компания. Такое пространство не в переключателе — канал виден у себя в «Общих каналах». */
  external: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
});
export type MyMembership = z.infer<typeof myMembershipSchema>;

/** Свои пространства — без тех, куда человек пришёл гостем из другой компании (общие каналы). */
export const ownMemberships = (list: readonly MyMembership[]): MyMembership[] => list.filter((m) => m.external === null);

export const meSchema = z.object({ user: userSchema, memberships: z.array(myMembershipSchema) });
export type Me = z.infer<typeof meSchema>;

/** Способы второго шага у человека — экран входа показывает нужные. */
export const secondFactorsSchema = z.object({ totp: z.boolean(), passkey: z.boolean(), recovery: z.boolean() });
export type SecondFactors = z.infer<typeof secondFactorsSchema>;

export const loginResultSchema = z.union([z.object({ twoFactorRequired: z.literal(true), methods: secondFactorsSchema.default({ totp: true, passkey: false, recovery: false }) }), meSchema]);

/** Приглашение по ссылке из письма: кого, куда и кто позвал — до входа. */
export const invitationSchema = z.object({
  email: z.string(),
  name: z.string(),
  workspace: z.object({ id: z.string(), name: z.string() }),
  inviter: z.string().nullable(),
  members: z.number().int(),
  people: z.array(z.string()),
  expiresAt: z.string(),
});

/** Способы входа, которые включены на сервере. */
/** demo — сервер открывает сотрудникам пространство с демо-перепиской («Войти с демо-данными»). */
export const authMethodsSchema = z.object({ password: z.boolean(), google: z.boolean(), demo: z.boolean().default(false), phone: z.boolean().default(false) });
export type AuthMethods = z.infer<typeof authMethodsSchema>;

export const kindSchema = z.enum(['public', 'private', 'direct', 'group']);

export const conversationSchema = z.object({
  id: z.uuid(),
  kind: kindSchema,
  name: z.string().nullable(),
  topic: z.string().nullable(),
  description: z.string().nullable(),
  isGeneral: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
  createdById: z.string().nullable(),
  /** Секретный чат: сообщения шифруются на устройствах, в этот момент чат исчезнет у всех. */
  secretUntil: z.string().nullable().default(null),
  /** Секретный чат по ссылке: в нём, кроме людей из пространства, могут быть гости без аккаунта. */
  secretLink: z.boolean().optional(),
  lastSeq: z.number().int(),
  lastMessageAt: z.string().nullable(),
  memberCount: z.number().int(),
  memberIds: z.array(z.string()),
  isMember: z.boolean(),
  lastReadSeq: z.number().int(),
  starred: z.boolean(),
  muted: z.boolean(),
  /** Звук выключен до этого времени; null — навсегда (или звук включён). */
  mutedUntil: z.string().nullable().default(null),
  hidden: z.boolean(),
  /** «Больше не показывать такие уведомления в канале»: служебные строки скрыты у этого человека. */
  hideSystem: z.boolean().optional(),
  notify: notifyPrefSchema,
  unread: z.number().int(),
  mentions: z.number().int(),
  /** Свой раздел боковой панели; null — стандартный («Каналы» или «Личные сообщения»). */
  sectionId: z.string().nullable().default(null),
  /** Владелец канала по списку участников. */
  channelOwner: z.boolean().default(false),
  /** Последнее сообщение личной переписки — для строки в списке «Личные сообщения». */
  lastMessage: z.object({ id: z.string().optional(), authorId: z.string().nullable(), text: z.string(), kind: z.enum(['text', 'file', 'secret', 'system']) }).nullable().default(null),
});
export type Conversation = z.infer<typeof conversationSchema>;

/** Папка боковой панели (до 0.28 — «раздел»): вложенная (parentId), со значком (эмодзи или иконка с цветом) и свёрнутостью. */
export const sectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number().int(),
  parentId: z.string().nullable().default(null),
  emoji: z.string().nullable().default(null),
  icon: z.string().nullable().default(null),
  color: z.string().nullable().default(null),
  collapsed: z.boolean().default(false),
});
export type SidebarSection = z.infer<typeof sectionSchema>;

export const sidebarSchema = z.object({ conversations: z.array(conversationSchema), threadsUnread: z.number().int(), sections: z.array(sectionSchema).default([]) });
export type Sidebar = z.infer<typeof sidebarSchema>;

/** «Умные папки»: темы со словами, расклад каналов по темам (почему туда — слова), закрепления. */
export const smartTopicSchema = z.object({ key: z.string(), name: z.string(), emoji: z.string(), keywords: z.array(z.string()) });
export type SmartTopic = z.infer<typeof smartTopicSchema>;
export const smartAssignmentSchema = z.object({
  conversationId: z.string(),
  topic: z.string(),
  words: z.array(z.string()),
  score: z.number(),
  reason: z.enum(['words', 'few', 'fallback', 'ai']),
  messages: z.number().int(),
  pinned: z.boolean(),
});
export type SmartAssignment = z.infer<typeof smartAssignmentSchema>;
export const smartFoldersSchema = z.object({
  enabled: z.boolean(),
  useAi: z.boolean(),
  aiAvailable: z.boolean(),
  aiError: z.string().nullable(),
  customized: z.boolean(),
  topics: z.array(smartTopicSchema),
  minMessages: z.number().int(),
  computedAt: z.string().nullable(),
  assignments: z.array(smartAssignmentSchema),
  pins: z.record(z.string(), z.string()),
});
export type SmartFolders = z.infer<typeof smartFoldersSchema>;

/** Кто может писать в канал (как «Posting permissions» в Slack) и что из этого можно мне. */
export const postingSchema = z.object({
  policy: z.enum(['everyone', 'admins', 'selected']),
  threads: z.enum(['everyone', 'same']),
  apps: z.boolean(),
  reactions: z.boolean(),
  memberNotices: z.boolean(),
  /** Автоперевод канала: каждый читает на своём языке. */
  autoTranslate: z.boolean().default(false),
  canPost: z.boolean(),
  canReply: z.boolean(),
  canReact: z.boolean(),
  roles: z.array(z.object({ userId: z.string(), role: z.enum(['owner', 'poster']) })),
});
export type Posting = z.infer<typeof postingSchema>;

export const conversationDetailsSchema = conversationSchema.extend({ allMemberIds: z.array(z.string()), canManage: z.boolean(), posting: postingSchema.optional() });
export type ConversationDetails = z.infer<typeof conversationDetailsSchema>;

export const directoryEntrySchema = z.object({
  id: z.uuid(),
  kind: kindSchema,
  name: z.string().nullable(),
  topic: z.string().nullable(),
  description: z.string().nullable(),
  isGeneral: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
  lastMessageAt: z.string().nullable(),
  memberCount: z.number().int(),
  isMember: z.boolean(),
  /** Канал скрыт из левой панели (человек в нём состоит, вернуть — в «Все каналы»). */
  hidden: z.boolean().default(false),
  /** Кто создал канал (нет — создан системой или человек удалён). */
  createdById: z.string().nullable().default(null),
});
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;

/** Документ редактора (ProseMirror). Сервер уже проверил и очистил его. */
export interface DocNode {
  readonly type: string;
  readonly text?: string | undefined;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
  readonly marks?: readonly { readonly type: string; readonly attrs?: Readonly<Record<string, unknown>> | undefined }[] | undefined;
  readonly content?: readonly DocNode[] | undefined;
}
export const docNodeSchema: z.ZodType<DocNode> = z.lazy(() =>
  z.object({
    type: z.string(),
    text: z.string().optional(),
    attrs: z.record(z.string(), z.unknown()).optional(),
    marks: z.array(z.object({ type: z.string(), attrs: z.record(z.string(), z.unknown()).optional() })).optional(),
    content: z.array(docNodeSchema).optional(),
  }),
);

export const attachmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mime: z.string(),
  size: z.number().int(),
  isImage: z.boolean(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  uploaderId: z.string().nullable(),
  createdAt: z.string(),
  /** Голосовое и «кружок» — записи с телефона; всё остальное — обычный файл. */
  kind: z.enum(['file', 'voice', 'circle']),
  durationMs: z.number().int().nullable(),
  /** Высота полосок волны, 0–100. */
  waveform: z.array(z.number().int()).nullable(),
  transcript: z.string().nullable(),
});
export type Attachment = z.infer<typeof attachmentSchema>;

export const reactionSchema = z.object({ emoji: z.string(), userIds: z.array(z.string()) });
export type Reaction = z.infer<typeof reactionSchema>;

/** Снимок пересланного сообщения: остаётся, даже если оригинал изменят. */
export const sharedSchema = z.object({
  messageId: z.string(),
  conversationId: z.string(),
  conversationName: z.string().nullable(),
  authorId: z.string().nullable(),
  content: docNodeSchema.nullable(),
  text: z.string(),
  createdAt: z.string(),
});
export type SharedMessage = z.infer<typeof sharedSchema>;

/** Цитата, как в Telegram: на какое сообщение ответ и какой кусок его текста процитирован (снимок на момент отправки). */
export const quoteSchema = z.object({
  messageId: z.string(),
  authorId: z.string().nullable(),
  /** Имя автора из моста Slack, если сообщение пришло оттуда. */
  authorName: z.string().nullable(),
  threadRootId: z.string().nullable(),
  text: z.string(),
  /** Процитировано сообщение целиком (начало), а не выделенный кусок. */
  whole: z.boolean(),
});
export type Quote = z.infer<typeof quoteSchema>;

/** Открытый ключ устройства (EC P-256, JWK). */
export const publicJwkSchema = z.object({ kty: z.literal('EC'), crv: z.literal('P-256'), x: z.string(), y: z.string() });

/** Зашифрованный пароль в сообщении: сервер хранит только шифротекст и ключи для устройств. */
export const secretSchema = z.object({
  id: z.string(),
  /** password — карточка пароля, message — сообщение секретного чата. */
  kind: z.enum(['password', 'message']).default('password'),
  alg: z.string(),
  senderId: z.string(),
  iv: z.string().nullable(),
  ciphertext: z.string().nullable(),
  keys: z.array(z.object({ d: z.string(), e: publicJwkSchema, i: z.string(), k: z.string() })),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  state: z.enum(['ok', 'revoked', 'expired']),
});

export const deviceSchema = z.object({
  id: z.string(),
  name: z.string(),
  publicKey: publicJwkSchema,
  createdAt: z.string(),
  lastSeenAt: z.string(),
});
export type Device = z.infer<typeof deviceSchema>;

export const recipientKeysSchema = z.object({
  devices: z.array(z.object({ userId: z.string(), deviceId: z.string(), name: z.string(), publicKey: publicJwkSchema })),
});

/** Как показывать превью ссылки в сообщении: размер, место (над текстом или под ним) и какая ссылка. Нет настройки — как обычно. */
export const linkPreviewSettingsSchema = z.object({ mode: z.enum(['large', 'small', 'off']), above: z.boolean(), url: z.string().nullable() });
export type LinkPreviewSettings = z.infer<typeof linkPreviewSettingsSchema>;

export const messageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  seq: z.number().int(),
  authorId: z.string().nullable(),
  // Мост Slack: пишет бот приложения, а показывается настоящий автор (userId — если почта совпала с пользователем Chat).
  bridgeAuthor: z.object({ name: z.string(), avatar: z.string().nullable(), userId: z.string().nullable() }).nullish(),
  // Мост Slack: почему сообщение не дошло до Slack (видит и повторяет автор).
  bridgeFailure: z.string().nullish(),
  systemCode: z.string().nullable(),
  // PHP кодирует пустой объект как {} (stdClass), а список — как [].
  systemData: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]),
  content: docNodeSchema.nullable(),
  text: z.string(),
  threadRootId: z.string().nullable(),
  replyCount: z.number().int(),
  lastReplyAt: z.string().nullable(),
  lastReplySeq: z.number().int(),
  replyUserIds: z.array(z.string()),
  alsoInChannel: z.boolean(),
  shared: sharedSchema.nullable().default(null),
  quote: quoteSchema.nullable().default(null),
  secret: secretSchema.nullable().default(null),
  /** Опрос: сообщение — сам опрос, текст — его вопрос (для превью, поиска и уведомлений). */
  pollId: z.string().nullable().default(null),
  /** Запрос согласования: сообщение — его карточка, текст — название. */
  approvalId: z.string().nullable().default(null),
  /** Анкета из чата: сообщение — её карточка, текст — название. */
  formId: z.string().nullable().default(null),
  editedAt: z.string().nullable(),
  /** «Общий текст»: править могут все в беседе; версии — GET /messages/{id}/revisions. */
  linkPreview: linkPreviewSettingsSchema.nullable().default(null),
  collab: z.boolean().default(false),
  collabEditorIds: z.array(z.string()).default([]),
  lastEditorId: z.string().nullable().default(null),
  revisionCount: z.number().int().default(0),
  /** Растёт с каждой правкой общего текста — правка присылает его как baseVersion. */
  editVersion: z.number().int().default(0),
  deleted: z.boolean(),
  pinnedAt: z.string().nullable(),
  pinnedById: z.string().nullable(),
  createdAt: z.string(),
  reactions: z.array(reactionSchema),
  attachments: z.array(attachmentSchema),
});
export type Message = z.infer<typeof messageSchema>;

/** Версия общего текста: кто правил и каким текст стал. Первая — как отправили. */
export const revisionSchema = z.object({ id: z.string(), authorId: z.string().nullable(), content: docNodeSchema.nullable(), text: z.string(), at: z.string() });
export type Revision = z.infer<typeof revisionSchema>;

export const messagePageSchema = z.object({ messages: z.array(messageSchema), hasMoreBefore: z.boolean(), hasMoreAfter: z.boolean() });
export type MessagePage = z.infer<typeof messagePageSchema>;

export const threadSchema = z.object({ root: messageSchema, replies: z.array(messageSchema), following: z.boolean(), lastReadSeq: z.number().int().nullable() });
export type Thread = z.infer<typeof threadSchema>;

export const threadSummarySchema = z.object({ root: messageSchema, replies: z.array(messageSchema), unread: z.boolean(), lastReadSeq: z.number().int() });
export type ThreadSummary = z.infer<typeof threadSummarySchema>;

/** Дело в Todo (из сообщения или своё) или сохранённое сообщение, как в Slack. */
export const todoSchema = z.object({
  id: z.string(),
  kind: z.enum(['todo', 'saved']),
  title: z.string().nullable(),
  message: messageSchema.nullable(),
  done: z.boolean(),
  completedAt: z.string().nullable(),
  remindAt: z.string().nullable(),
  remindedAt: z.string().nullable(),
  createdAt: z.string(),
  /** Приоритет 0–3, своя заметка и кто поручил (с 0.28; старый сервер их не присылает). */
  priority: z.number().int().default(0),
  note: z.string().nullable().default(null),
  assignedById: z.string().nullable().default(null),
});
export type Todo = z.infer<typeof todoSchema>;

export const activityItemSchema = z.object({
  /** Пункт по ключу: «mention:<сообщение>», «thread:…», «invite:…», «reminder:…» или «reaction:<сообщение>:<человек>:<эмодзи>». */
  key: z.string(),
  read: z.boolean().default(false),
  /** message — любое сообщение выбранных людей или бесед (только в своих видах, вид «Все сообщения»). */
  type: z.enum(['mention', 'reaction', 'thread', 'invite', 'reminder', 'keyword', 'channel', 'message']),
  /** Пункт убран (по одному или «Очистить всё») — приходит только в списке «Убранное». */
  cleared: z.boolean().default(false),
  at: z.string(),
  userId: z.string().nullable(),
  emoji: z.string().nullable(),
  message: messageSchema,
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

export const searchResultSchema = z.object({ messages: z.array(messageSchema), hasMore: z.boolean() });

export const fileEntrySchema = attachmentSchema.extend({ messageId: z.string().nullable(), conversationId: z.string().nullable().optional() });
export type FileEntry = z.infer<typeof fileEntrySchema>;

export const scheduledSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  threadRootId: z.string().nullable(),
  alsoInChannel: z.boolean(),
  content: docNodeSchema.nullable(),
  text: z.string(),
  attachmentCount: z.number().int(),
  sendAt: z.string(),
  error: z.string().nullable(),
  createdAt: z.string(),
  /** Повтор по дням недели (1 — пн … 7 — вс); пусто — отправится один раз. */
  repeatDays: z.array(z.number().int()).default([]),
  timezone: z.string().nullable().default(null),
});

export const bookmarkSchema = z.object({ id: z.string(), title: z.string(), url: z.string(), createdById: z.string().nullable(), createdAt: z.string() });

export const realtimeEventSchema = z.object({
  id: z.number().int(),
  type: z.string(),
  conversationId: z.string().nullable(),
  payload: z.unknown(),
});
export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;
export const eventsSchema = z.object({ events: z.array(realtimeEventSchema), cursor: z.number().int() });

export const auditEntrySchema = z.object({
  id: z.uuid(),
  action: z.enum(['create', 'update', 'delete', 'login', 'security']),
  entity: z.string(),
  entityId: z.string().nullable(),
  userId: z.string().nullable(),
  changes: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]),
  createdAt: z.string(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export const auditPageSchema = z.object({ items: z.array(auditEntrySchema), nextCursor: z.string().nullable() });

export const totpSetupSchema = z.object({ secret: z.string(), uri: z.string() });

/** Ключ доступа (WebAuthn) в профиле. */
export const passkeySchema = z.object({ id: z.string(), name: z.string(), createdAt: z.string(), lastUsedAt: z.string().nullable() });

/** Есть ли у человека второй шаг входа (старый сервер присылает только totpEnabled). */
export function hasSecondFactor(user: User): boolean {
  return user.twoFactor ?? (user.totpEnabled || user.passkeys > 0);
}
